import React, { useState } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosClient from '../../services/axiosClient';
import './ClearCache.css';

const ClearCache = () => {
  const [isClearing, setIsClearing] = useState(false);

  const handleClearCache = async () => {
    const result = await Swal.fire({
      title: 'Vider le cache ?',
      text: 'Êtes-vous sûr de vouloir vider le cache ? Cette action peut prendre quelques instants.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#145650',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, vider le cache',
      cancelButtonText: 'Annuler'
    });

    if (result.isConfirmed) {
      setIsClearing(true);
      try {
        // axiosClient gère automatiquement X-AppApiToken et Authorization
        const response = await axiosClient.post('/admin/settings/cache/clear');

        if (response.status === 200) {
          toast.success('Cache vidé avec succès !', {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
          });
        } else {
          toast.error(response.data?.message || 'Erreur lors du vidage du cache', {
            position: 'top-right',
            autoClose: 3000
          });
        }
      } catch (error) {
        console.error('Erreur:', error);
        toast.error('Erreur de connexion au serveur', {
          position: 'top-right',
          autoClose: 3000
        });
      } finally {
        setIsClearing(false);
      }
    }
  };

  return (
    <div className="admin-cache-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h2 className="page-title">
              <i className="fa fa-sync-alt me-3"></i>
              Vider le Cache
            </h2>
            <p className="page-subtitle">
              Gérer et optimiser les performances de la plateforme
            </p>
          </div>
        </div>
      </div>

      {/* Cache Info Cards */}
      <Row className="mb-4">
        <Col xl={3} lg={6} sm={6}>
          <Card className="cache-info-card card-primary">
            <Card.Body>
              <div className="cache-icon">
                <i className="fa fa-database"></i>
              </div>
              <div className="cache-content">
                <h4 className="cache-title">Cache Application</h4>
                <p className="cache-detail">Données temporaires</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} sm={6}>
          <Card className="cache-info-card card-secondary">
            <Card.Body>
              <div className="cache-icon">
                <i className="fa fa-route"></i>
              </div>
              <div className="cache-content">
                <h4 className="cache-title">Cache Routes</h4>
                <p className="cache-detail">Routes compilées</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} sm={6}>
          <Card className="cache-info-card card-success">
            <Card.Body>
              <div className="cache-icon">
                <i className="fa fa-cog"></i>
              </div>
              <div className="cache-content">
                <h4 className="cache-title">Cache Config</h4>
                <p className="cache-detail">Configuration</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col xl={3} lg={6} sm={6}>
          <Card className="cache-info-card card-warning">
            <Card.Body>
              <div className="cache-icon">
                <i className="fa fa-eye"></i>
              </div>
              <div className="cache-content">
                <h4 className="cache-title">Cache Views</h4>
                <p className="cache-detail">Vues compilées</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card className="cache-content-card">
        <Card.Body>
          <div className="cache-main-content">
            <div className="cache-illustration">
              <i className="fa fa-broom fa-5x"></i>
            </div>
            
            <h3 className="cache-main-title">Vider le cache de l'application</h3>
            <p className="cache-main-description">
              Le vidage du cache permet d'améliorer les performances de la plateforme en supprimant 
              les données temporaires obsolètes. Cette opération est recommandée après une mise à jour 
              ou en cas de problème d'affichage.
            </p>

            <div className="cache-features">
              <Row>
                <Col md={6} className="mb-3">
                  <div className="feature-item">
                    <i className="fa fa-check-circle me-3"></i>
                    <span>Supprime les données temporaires</span>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="feature-item">
                    <i className="fa fa-check-circle me-3"></i>
                    <span>Améliore les performances</span>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="feature-item">
                    <i className="fa fa-check-circle me-3"></i>
                    <span>Résout les problèmes d'affichage</span>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="feature-item">
                    <i className="fa fa-check-circle me-3"></i>
                    <span>Actualise la configuration</span>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="cache-action">
              <Button 
                className="btn-clear-cache" 
                onClick={handleClearCache}
                disabled={isClearing}
              >
                {isClearing ? (
                  <>
                    <i className="fa fa-spinner fa-spin me-2"></i>
                    Vidage en cours...
                  </>
                ) : (
                  <>
                    <i className="fa fa-broom me-2"></i>
                    Vider le cache maintenant
                  </>
                )}
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Warning Card */}
      <Card className="warning-card mt-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col lg={1} className="text-center">
              <i className="fa fa-exclamation-triangle fa-3x"></i>
            </Col>
            <Col lg={11}>
              <h4>Important</h4>
              <p className="mb-0">
                Le vidage du cache peut temporairement ralentir l'application pendant que les nouvelles 
                données sont générées. Il est recommandé d'effectuer cette opération pendant les heures 
                de faible affluence.
              </p>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
};

export default ClearCache;
